// src/components/lead-tracker/SharingDialog.jsx
// Owner-only. Toggles features.leadTracker on user docs — DOT NOTATION so the
// nested features map is patched, never replaced (protects impactScorecard).
// Recipients see the tab after their next reload/login (user doc is a
// one-shot fetch at login).
import React, { useState } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { getAllusers, updateUserProfile } from "../../utils/firebasefunctions";
import { isSuperAdminOwner } from "../../utils/brokerageSettings";

export default function SharingDialog({ onClose }) {
  const [{ users }, dispatch] = useStateValue();
  const [busyId, setBusyId] = useState("");

  const eligible = (users || []).filter(
    (u) => u?.type !== 1 && u?.type !== 2 && !isSuperAdminOwner(u),
  );
  const clientCare = eligible.filter((u) => u.role === "Supervisor");
  const others = eligible.filter((u) => u.role !== "Supervisor");

  const refresh = async () => {
    const response = await getAllusers("users");
    dispatch({
      type: actionType.STORE_ALLUSERS,
      teamleads: response.filter((d) => d.role === "Admin"),
      clients: response.filter((d) => d.role === "Supervisor"),
      employees: response,
      allusers: response,
    });
  };

  const toggle = async (u) => {
    const next = !(u.features?.leadTracker === true);
    setBusyId(u.id);
    try {
      const ok = await updateUserProfile(u.id, { "features.leadTracker": next });
      if (ok) {
        await refresh();
        toast.success(
          next
            ? `${u.firstname || u.name || "User"} can now see the Lead Tracker (after their next sign-in or reload)`
            : `Lead Tracker access removed for ${u.firstname || u.name || "user"}`,
        );
      } else {
        toast.error("Couldn't update access");
      }
    } catch (e) {
      // refresh (getAllusers) can throw on a network failure; without this
      // the Share button stayed disabled forever.
      toast.error("Couldn't refresh the user list. Please reload the page.");
    } finally {
      setBusyId("");
    }
  };

  const Row = ({ u }) => (
    <div className="flex items-center justify-between py-2 border-t border-slate-100">
      <div>
        <div className="text-sm font-semibold text-slate-800">
          {[u.firstname, u.lastname].filter(Boolean).join(" ") || u.name || u.email}
        </div>
        <div className="text-[11px] text-slate-400">
          {u.role === "Supervisor" ? "Client Care" : u.role === "Admin" ? "Team Lead" : "Agent"}
        </div>
      </div>
      <button
        disabled={busyId === u.id}
        onClick={() => toggle(u)}
        className={`text-xs rounded-full px-3 py-1 font-semibold ${
          u.features?.leadTracker === true
            ? "bg-emerald-100 text-emerald-700"
            : "bg-slate-100 text-slate-500"
        } disabled:opacity-50`}
      >
        {u.features?.leadTracker === true ? "Shared ✓" : "Share"}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-slate-900">Share the Lead Tracker</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Shared people see YOUR tracker and can fix row details. Goals and
          sharing stay yours only. Changes apply on their next sign-in or reload.
        </p>
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
          Client Care
        </div>
        {clientCare.length === 0 && (
          <div className="text-xs text-slate-400 py-2">No Client Care users</div>
        )}
        {clientCare.map((u) => <Row key={u.id} u={u} />)}
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mt-4">
          Everyone else
        </div>
        {others.map((u) => <Row key={u.id} u={u} />)}
      </div>
    </div>
  );
}
